import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui";

export type TaskStatus =
  | "pending"
  | "in-progress"
  | "completed"
  | "overdue"
  | "approved"
  | "rejected";

interface TaskItem {
  id: string;
  title: string;
  subtitle?: string;
  status: TaskStatus;
  metadata?: {
    [key: string]: string | number | Date;
  };
  priority?: "low" | "medium" | "high";
}

interface AnalyticsTaskListProps {
  title: string;
  description?: string;
  tasks: TaskItem[];
  className?: string;
  showMetadata?: boolean;
  metadataLabels?: {
    [key: string]: string;
  };
  onTaskClick?: (task: TaskItem) => void;
}

export const AnalyticsTaskList: React.FC<AnalyticsTaskListProps> = ({
  title,
  description,
  tasks,
  className = "",
  showMetadata = true,
  metadataLabels = {},
  onTaskClick,
}) => {
  // Get status color and icon
  const getStatusStyles = (
    status: TaskStatus
  ): { color: string; icon: string } => {
    switch (status) {
      case "pending":
        return {
          color:
            "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-100",
          icon: "⌛",
        };
      case "in-progress":
        return {
          color:
            "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-100",
          icon: "🔄",
        };
      case "completed":
        return {
          color:
            "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-100",
          icon: "✅",
        };
      case "overdue":
        return {
          color: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-100",
          icon: "⚠️",
        };
      case "approved":
        return {
          color:
            "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-100",
          icon: "✓",
        };
      case "rejected":
        return {
          color: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-100",
          icon: "✗",
        };
      default:
        return {
          color:
            "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200",
          icon: "•",
        };
    }
  };

  // Get priority indicator
  const getPriorityIndicator = (
    priority?: "low" | "medium" | "high"
  ): { color: string; text: string } => {
    switch (priority) {
      case "high":
        return {
          color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
          text: "High",
        };
      case "medium":
        return {
          color:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
          text: "Medium",
        };
      case "low":
        return {
          color:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
          text: "Low",
        };
      default:
        return { color: "", text: "" };
    }
  };

  // Format date values
  const formatValue = (value: any): string => {
    if (value instanceof Date) {
      return (
        value.toLocaleDateString() +
        " " +
        value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    }
    return String(value);
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {tasks.length === 0 ? (
            <div className="py-4 text-center text-muted-foreground">
              No tasks to display
            </div>
          ) : (
            tasks.map((task) => {
              const { color, icon } = getStatusStyles(task.status);
              const priority = getPriorityIndicator(task.priority);

              return (
                <div
                  key={task.id}
                  className={`py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
                    onTaskClick
                      ? "cursor-pointer hover:bg-muted/30 px-2 -mx-2 rounded-md transition-colors"
                      : ""
                  }`}
                  onClick={() => onTaskClick && onTaskClick(task)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{task.title}</h3>
                      {priority.text && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${priority.color}`}
                        >
                          {priority.text}
                        </span>
                      )}
                    </div>
                    {task.subtitle && (
                      <p className="text-sm text-muted-foreground">
                        {task.subtitle}
                      </p>
                    )}

                    {/* Task metadata */}
                    {showMetadata && task.metadata && (
                      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                        {Object.entries(task.metadata).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex items-baseline text-xs"
                          >
                            <span className="font-medium mr-1">
                              {metadataLabels[key] || key}:
                            </span>
                            <span className="text-muted-foreground">
                              {formatValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div
                    className={`px-3 py-1 rounded-full text-xs flex items-center ${color}`}
                  >
                    <span className="mr-1.5">{icon}</span>
                    <span className="capitalize">
                      {task.status.replace("-", " ")}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};
